/*
 * pdp-grid.js
 * ---------------------------------------------------------------
 * Quick add on the best-seller cards.
 *
 * The button used to sit inside the product link, so pressing it
 * navigated to the product page instead of adding anything. It posts to
 * the cart now, updates the count in the header and confirms on the
 * button itself -- no modal, because the point of a quick add is that
 * the browse is not interrupted.
 *
 * Only single-variant products render a button at all; anything with a
 * choice to make links through to its page. Silently adding the first
 * of three pack sizes is a returned order, not a conversion.
 *
 * Vanilla ES6, no dependencies. Mirrors assets/pdp-explore.js so the two
 * quick-adds on the site behave identically.
 * ---------------------------------------------------------------
 */

(function () {
  'use strict';

  /* The click handler is delegated to `document`, so a second copy of this
     file on the page registers a second listener and every quick add fires
     twice. Two sections load it now -- the card grid and the bundle row --
     so the file has to be safe to include more than once. */
  if (window.__pdpGridReady) {
    return;
  }
  window.__pdpGridReady = true;

  var RESET_AFTER = 2200;

  /* One live region for the whole document, made on first use.

     It used to be looked up inside the section, which meant it only
     existed where the homepage grid had written one -- so on a
     collection page nothing was ever announced. */
  function announce(message) {
    var live = document.querySelector('[data-pgrid-status]');
    if (!live) {
      live = document.createElement('p');
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('data-pgrid-status', '');
      document.body.appendChild(live);
    }
    live.textContent = message;
  }

  /* The cart notification is switched off in this theme, so the header
     count is the only feedback the page can give. Updated directly, and
     an event dispatched for anything else listening. */
  function refreshCart() {
    fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        var bubble = document.querySelector('.cart-count-bubble');
        if (bubble && cart.item_count > 0) bubble.hidden = false;
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
      })
      .catch(function () {
        /* It is in the cart either way; a stale badge is not worth an
           error message. */
      });
  }

  function add(button) {
    var id = button.getAttribute('data-variant-id');
    if (!id || button.classList.contains('is-busy')) return;

    button.classList.add('is-busy');
    button.disabled = true;

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) throw new Error(body.description || body.message || 'Could not add');
          return body;
        });
      })
      .then(function () {
        button.classList.remove('is-busy');
        button.classList.add('is-added');
        button.disabled = false;

        /* The label changes with the state. A tick beside the words
           "Add to cart" reads as an instruction that has not happened
           yet; "Added" says it has. */
        var label = button.querySelector('.pgrid__cta-label');
        if (label && !label.dataset.idle) {
          label.dataset.idle = label.textContent.trim();
          label.textContent = 'Added';
        }

        announce((button.getAttribute('data-title') || 'Item') + ' added to your cart');
        refreshCart();

        window.setTimeout(function () {
          button.classList.remove('is-added');
          if (label && label.dataset.idle) {
            label.textContent = label.dataset.idle;
            delete label.dataset.idle;
          }
        }, RESET_AFTER);
      })
      .catch(function (error) {
        button.classList.remove('is-busy');
        button.disabled = false;
        announce(error.message || 'Sorry, that could not be added.');
      });
  }

  /* Bound once, on the document.

     The listener used to be attached to each `.pgrid` section -- a
     class that exists on the homepage grid and nowhere else. The same
     card renders on collection pages, search and related products, so
     thirteen add buttons sat there with nothing listening to them.
     Delegating from the document covers every one of them, including
     any added later by the theme editor or a filter reload. */
  function bind() {
    if (document.documentElement.dataset.pgridBound === 'true') return;
    document.documentElement.dataset.pgridBound = 'true';

    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-pgrid-add]');
      if (!button) return;
      /* The button sits over a stretched card link; without this the
         click would follow the card through to the product page. */
      event.preventDefault();
      event.stopPropagation();
      add(button);
    });
  }

  function init() {
    bind();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () { init(); });
})();

/* Which card is in view ---------------------------------------------------
 *
 * In slider mode the card the reader is looking at takes its own
 * background, so it separates from the ones resting on the ground beside
 * it. There is no CSS selector for "the snapped one", so an
 * IntersectionObserver marks it.
 *
 * Thresholds rather than a scroll listener: the browser reports only when
 * a card crosses one of them, off the main thread, instead of the page
 * doing arithmetic on every frame of a flick.
 */
(function () {
  function watch(track) {
    if (track.dataset.pgridWatched === 'true') return;
    track.dataset.pgridWatched = 'true';

    var items = Array.prototype.slice.call(track.querySelectorAll('.pgrid__item'));
    if (items.length < 2) return;

    var ratios = new WeakMap();

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          ratios.set(entry.target, entry.intersectionRatio);
        });

        /* The most-visible card wins. On a track where two are half in
           view this picks the leading one, which is the one the flick was
           heading towards. */
        var best = null;
        var bestRatio = 0;
        items.forEach(function (item) {
          var r = ratios.get(item) || 0;
          if (r > bestRatio) {
            bestRatio = r;
            best = item;
          }
        });

        items.forEach(function (item) {
          item.classList.toggle('is-active', item === best);
        });
      },
      { root: track, threshold: [0, 0.25, 0.5, 0.75, 0.9, 1] }
    );

    items.forEach(function (item) {
      io.observe(item);
    });
  }

  function initTracks() {
    document.querySelectorAll('[data-pgrid-track]').forEach(watch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTracks);
  } else {
    initTracks();
  }

  document.addEventListener('shopify:section:load', initTracks);
})();
